import { HttpApiService } from "@/shared/services/httpApi.service";

import { ACCESS_ENDPOINTS } from "../constants/accessCatalog.constants";
import type {
  AccessPaginated,
  AccessRole,
  CreateRoleRequest,
  UpdateRoleRequest,
} from "../types/access.types";

const ROLES_PAGE_SIZE = 100;
/** Safety bound on pagination loops (ROLES_PAGE_SIZE × this = max roles loaded). */
const MAX_ROLE_PAGES = 50;

/**
 * `GET /internal/roles` — the roles catalog, each role with its policies (and their statements) embedded.
 * This is the permission graph the deny-first evaluator and the effective-access matrix read from.
 */
export const rolesService = {
  list: async (skip = 0, limit = ROLES_PAGE_SIZE): Promise<AccessPaginated<AccessRole>> => {
    const response = await HttpApiService.get<AccessPaginated<AccessRole>>(ACCESS_ENDPOINTS.ROLES, {
      params: { skip, limit },
    });
    return response.data;
  },

  /**
   * Fetch the WHOLE catalog by paging through until exhausted. Required for correctness: the backend seeds a
   * base role per module×action (one per cell of the access catalog, ~84 today) plus named roles, so a single
   * page would truncate it — and a principal's role missing from the catalog would skew its effective access.
   * Bounded by MAX_ROLE_PAGES so a misbehaving `hasMore` can't loop forever.
   */
  listAll: async (): Promise<AccessRole[]> => {
    const all: AccessRole[] = [];
    let skip = 0;
    for (let page = 0; page < MAX_ROLE_PAGES; page++) {
      const result = await rolesService.list(skip, ROLES_PAGE_SIZE);
      all.push(...result.data);
      if (!result.hasMore || result.data.length === 0) break;
      skip += ROLES_PAGE_SIZE;
    }
    return all;
  },

  /** `POST /internal/roles` — slug, scope and color are derived server-side. */
  create: async (body: CreateRoleRequest): Promise<AccessRole> => {
    const response = await HttpApiService.post<AccessRole, CreateRoleRequest>(ACCESS_ENDPOINTS.ROLES, body);
    return response.data;
  },

  /** `PATCH /internal/roles/:id` — `policy_ids`, when sent, replaces the set. */
  update: async (id: string, body: UpdateRoleRequest): Promise<AccessRole> => {
    const response = await HttpApiService.patch<AccessRole, UpdateRoleRequest>(
      ACCESS_ENDPOINTS.roleById(id),
      body,
    );
    return response.data;
  },

  /** `DELETE /internal/roles/:id` — cascades: also unassigns it from principals. */
  remove: async (id: string): Promise<void> => {
    await HttpApiService.delete(ACCESS_ENDPOINTS.roleById(id));
  },
};
